// In-app notifications for the shell's top-bar bell.
//
// Client wrappers for /v1/me/notifications + /v1/me/notification-settings
// and `useNotifications()`, which keeps one shared store per
// (person, active org). The shell remounts on every page, so the store
// lives at module level: a remount within CACHE_FRESH_MS reuses the
// last result instead of refetching. While the tab is visible the
// store polls every 60s; it also refreshes when the window regains
// focus or the tab becomes visible again.
//
// Not related to the legacy NotificationContext / NotificationBell
// (old chrome, local mock data).

import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { useAuth } from '../auth/AuthContext';
import { jsonOrThrow } from './apiError';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8080';

export type NotificationKind =
  | 'approval_requested'
  | 'connector_failing'
  | 'phishing_campaign_sent'
  | 'automated_action_taken'
  | 'exception_update'
  | 'umbra_decoy_hit'
  | 'umbra_spa_failed'
  | 'tracked_query_rise';

export type NotificationSeverity = 'info' | 'warning' | 'critical';

export interface AppNotification {
  id: string;
  kind: NotificationKind;
  title: string;
  body: string;
  /** In-app path. */
  href: string;
  severity: NotificationSeverity;
  app: string | null;
  createdAt: number;
  readAt: number | null;
}

export interface NotificationPage {
  notifications: AppNotification[];
  nextCursor: string | null;
  unreadCount: number;
}

export interface NotificationKindSetting {
  kind: NotificationKind;
  label: string;
  description: string;
  inApp: boolean;
  email: boolean;
}

export type NotificationSettingsPatch = Partial<
  Record<NotificationKind, { inApp?: boolean; email?: boolean }>
>;

const auth = (idToken: string) => ({ authorization: `Bearer ${idToken}` });

export async function listNotifications(
  idToken: string,
  opts: { cursor?: string; limit?: number; unreadOnly?: boolean } = {},
): Promise<NotificationPage> {
  const url = new URL(`${API_URL}/v1/me/notifications`);
  if (opts.cursor) url.searchParams.set('cursor', opts.cursor);
  if (opts.limit !== undefined) url.searchParams.set('limit', String(opts.limit));
  if (opts.unreadOnly) url.searchParams.set('unreadOnly', 'true');
  return jsonOrThrow(await fetch(url.toString(), { headers: auth(idToken) }));
}

export async function markNotificationRead(idToken: string, id: string): Promise<{ readAt: number }> {
  const res = await fetch(`${API_URL}/v1/me/notifications/${encodeURIComponent(id)}/read`, {
    method: 'POST',
    headers: auth(idToken),
  });
  return jsonOrThrow(res);
}

export async function markAllNotificationsRead(
  idToken: string,
): Promise<{ updated: number; remaining: boolean }> {
  const res = await fetch(`${API_URL}/v1/me/notifications/read-all`, {
    method: 'POST',
    headers: auth(idToken),
  });
  return jsonOrThrow(res);
}

export async function getNotificationSettings(
  idToken: string,
): Promise<{ kinds: NotificationKindSetting[] }> {
  return jsonOrThrow(await fetch(`${API_URL}/v1/me/notification-settings`, { headers: auth(idToken) }));
}

export async function updateNotificationSettings(
  idToken: string,
  kinds: NotificationSettingsPatch,
): Promise<{ kinds: NotificationKindSetting[] }> {
  const res = await fetch(`${API_URL}/v1/me/notification-settings`, {
    method: 'PUT',
    headers: { ...auth(idToken), 'content-type': 'application/json' },
    body: JSON.stringify({ kinds }),
  });
  return jsonOrThrow(res);
}

// ---------------- shared store ----------------

const POLL_MS = 60_000;
/** A remount inside this window reuses the cached page. */
const CACHE_FRESH_MS = 30_000;
const PAGE_SIZE = 20;

interface StoreState {
  key: string | null;
  items: AppNotification[];
  unreadCount: number;
  nextCursor: string | null;
  fetchedAt: number;
  loadingMore: boolean;
  error: string | null;
}

const EMPTY: StoreState = {
  key: null,
  items: [],
  unreadCount: 0,
  nextCursor: null,
  fetchedAt: 0,
  loadingMore: false,
  error: null,
};

let state: StoreState = EMPTY;
let inflight: { key: string; promise: Promise<void> } | null = null;
const listeners = new Set<() => void>();

function setState(patch: Partial<StoreState>) {
  state = { ...state, ...patch };
  for (const fn of listeners) fn();
}

function subscribe(fn: () => void) {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

const errorText = (err: unknown) => (err instanceof Error ? err.message : String(err));

async function fetchFirstPage(key: string, getToken: () => Promise<string>, force: boolean) {
  if (state.key !== key) state = { ...EMPTY, key };
  if (!force && Date.now() - state.fetchedAt < CACHE_FRESH_MS) return;
  if (inflight?.key === key) return inflight.promise;
  const promise = (async () => {
    try {
      const page = await listNotifications(await getToken(), { limit: PAGE_SIZE });
      if (state.key !== key) return;
      setState({
        items: page.notifications,
        unreadCount: page.unreadCount,
        nextCursor: page.nextCursor,
        fetchedAt: Date.now(),
        error: null,
      });
    } catch (err) {
      if (state.key === key) setState({ error: errorText(err) });
    } finally {
      // Only one load per key is ever in flight (see the early return).
      if (inflight?.key === key) inflight = null;
    }
  })();
  inflight = { key, promise };
  return promise;
}

export interface UseNotifications {
  items: AppNotification[];
  unreadCount: number;
  hasMore: boolean;
  /** True until the first page for this person + org has loaded. */
  loading: boolean;
  loadingMore: boolean;
  /** Message from the last failed load, null when the last load worked. */
  error: string | null;
  /** True once at least one page has loaded (items are real). */
  loaded: boolean;
  refresh: () => Promise<void>;
  loadMore: () => Promise<void>;
  /** Optimistic; reverts with a refetch if the API call fails, then rethrows. */
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
}

export function useNotifications(): UseNotifications {
  const { session } = useAuth();
  const key = session?.activeOrgId ? `${session.uid}:${session.activeOrgId}` : null;
  const snap = useSyncExternalStore(subscribe, () => state);
  const current = key && snap.key === key ? snap : { ...EMPTY, key };

  const getToken = useCallback(async () => {
    if (!session) throw new Error('Not signed in');
    return session.user.getIdToken();
  }, [session]);

  const refresh = useCallback(async () => {
    if (key) await fetchFirstPage(key, getToken, true);
  }, [key, getToken]);

  useEffect(() => {
    if (!key) return;
    void fetchFirstPage(key, getToken, false);
    const visible = () => document.visibilityState === 'visible';
    const timer = window.setInterval(() => {
      if (visible()) void fetchFirstPage(key, getToken, true);
    }, POLL_MS);
    const onFocus = () => void fetchFirstPage(key, getToken, true);
    const onVisibility = () => {
      if (visible()) void fetchFirstPage(key, getToken, false);
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [key, getToken]);

  const loadMore = useCallback(async () => {
    if (!key || state.key !== key || !state.nextCursor || state.loadingMore) return;
    setState({ loadingMore: true });
    try {
      const page = await listNotifications(await getToken(), { cursor: state.nextCursor, limit: PAGE_SIZE });
      if (state.key !== key) return;
      const seen = new Set(state.items.map((n) => n.id));
      setState({
        items: [...state.items, ...page.notifications.filter((n) => !seen.has(n.id))],
        nextCursor: page.nextCursor,
        unreadCount: page.unreadCount,
        error: null,
      });
    } catch (err) {
      if (state.key === key) setState({ error: errorText(err) });
    } finally {
      if (state.key === key) setState({ loadingMore: false });
    }
  }, [key, getToken]);

  const markRead = useCallback(
    async (id: string) => {
      if (!key || state.key !== key) return;
      const target = state.items.find((n) => n.id === id);
      if (!target || target.readAt !== null) return;
      const now = Date.now();
      setState({
        items: state.items.map((n) => (n.id === id ? { ...n, readAt: now } : n)),
        unreadCount: Math.max(0, state.unreadCount - 1),
      });
      try {
        await markNotificationRead(await getToken(), id);
      } catch (err) {
        await fetchFirstPage(key, getToken, true);
        throw err;
      }
    },
    [key, getToken],
  );

  const markAllRead = useCallback(async () => {
    if (!key || state.key !== key) return;
    const now = Date.now();
    setState({
      items: state.items.map((n) => (n.readAt === null ? { ...n, readAt: now } : n)),
      unreadCount: 0,
    });
    try {
      const token = await getToken();
      // The API marks up to ~1,800 per call and says if more remain.
      for (let i = 0; i < 5; i++) {
        const r = await markAllNotificationsRead(token);
        if (!r.remaining) break;
      }
    } finally {
      await fetchFirstPage(key, getToken, true);
    }
  }, [key, getToken]);

  return {
    items: current.items,
    unreadCount: current.unreadCount,
    hasMore: current.nextCursor !== null,
    loading: current.fetchedAt === 0 && !current.error,
    loadingMore: current.loadingMore,
    error: current.error,
    loaded: current.fetchedAt > 0,
    refresh,
    loadMore,
    markRead,
    markAllRead,
  };
}
